import { format, isAfter, isBefore, isSameDay, startOfDay } from 'date-fns';
import { z } from 'zod';
import {
  DateTimeGranularity,
  getDateTimeInputConfig,
  parseDateTimeInput,
  validateDateInputString,
} from '@/utilities/date';
import { type DAMNodeType, type NodeType, type TheNode, ancestorsSchema } from '@/types/tree';
import { type Asset, assetSchema, assetTagSchema, assetVariantsSchema } from '@/types/asset';
import { publicMetadataFieldSchema } from '@/types/metadata';
import { previewVariantsFieldSchema } from '@/types/variant';
import { paginationSchema } from '@/types/pagination';

export const shareAlbumSchema = z.object({
  id: z.string(),
  nodeId: z.string(),
  includeOriginalFile: z.boolean().nullable().optional(),
  downloadableVariants: z.array(previewVariantsFieldSchema).optional(),
  node: z.object({
    id: z.string(),
    name: z.string(),
    slug: z.string(),
    type: z.string(),
    album: z.object({
      createdAt: z.string(),
      updatedAt: z.string(),
      description: z.string(),
      assets: z.object({
        assets: z.array(assetSchema),
        pagination: paginationSchema,
      }),
    }),
  }),
});

// Accepts either the legacy single-string shape or the new multi-recipient
// array shape so the schema stays compatible across the backend rollout of
// DM-2267 (sharedWith stored in jsonb array). Consumers should normalize via
// the helper below.
export const sharedUserSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string(),
});

export type SharedUser = z.infer<typeof sharedUserSchema>;

export const nodeShareFieldSchema = z.object({
  id: z.string(),
  nodeId: z.string(),
  expiresAt: z.string().nullable(),
  sharedWith: z.union([z.string(), z.array(z.string())]).nullable(),
  includeOriginalFile: z.boolean().nullable().optional(),
  extraVariants: z.array(previewVariantsFieldSchema).nullable().optional(),
  previewVariant: previewVariantsFieldSchema.nullable().optional(),
  isPublic: z.boolean().nullable().optional(),
  collaborationEnabled: z.boolean().nullable().optional(),
  commentsVisibleFrom: z.string().nullable().optional(),
  sharedUsers: z.array(sharedUserSchema).nullable().optional(),
});

export const normalizeSharedWith = (value: string | Array<string> | null | undefined): Array<string> => {
  if (value === null || value === undefined) {
    return [];
  }
  return Array.isArray(value) ? value : [value];
};

export const shareSchema = z.object({
  nodeId: z.string(),
  name: z.string(),
  slug: z.string(),
  type: z.string(),
  ancestors: ancestorsSchema,
  count: z.number(),
  commentCount: z.number().optional().nullable(),
  variants: assetVariantsSchema
    .optional()
    .nullable()
    // api will return an empty array if there are no variants,
    // transform it into an empty object instead, to make it easier to work with
    .transform((arg) => (Array.isArray(arg) || arg === undefined ? {} : arg)),
});

export const nodeShareListSchema = z.object({
  shares: z.array(nodeShareFieldSchema),
  pagination: paginationSchema,
});

export const shareListSchema = z.object({
  shares: z.array(shareSchema),
  pagination: paginationSchema,
});

const CreateShareParams = z.object({
  nodeId: z.string(),
  body: z.object({
    sharedWith: z.array(z.string().email()).optional().nullable(),
    expiresAt: z.string().optional().nullable(),
    isPublic: z.boolean().optional().nullable(),
    collaborationEnabled: z.boolean().optional().nullable(),
    commentsVisibleFrom: z.string().optional().nullable(),
    includeOriginalFile: z.boolean().nullable().optional(),
    variants: z
      .object({
        preview: z.string().optional().nullable(),
        extra: z.array(z.string()).optional().nullable(),
      })
      .optional()
      .nullable(),
  }),
});

const UpdateShareParams = z.object({
  nodeId: z.string(),
  shareId: z.string(),
  body: z.object({
    sharedWith: z.array(z.string().email()).optional().nullable(),
    expiresAt: z.string().optional().nullable(),
    isPublic: z.boolean().optional().nullable(),
    collaborationEnabled: z.boolean().optional().nullable(),
    commentsVisibleFrom: z.string().optional().nullable(),
    includeOriginalFile: z.boolean().nullable().optional(),
    addEmails: z.array(z.string().email()).optional().nullable(),
    removeUserIds: z.array(z.string()).optional().nullable(),
    variants: z
      .object({
        preview: z.string().optional().nullable(),
        extra: z.array(z.string()).optional().nullable(),
      })
      .optional()
      .nullable(),
  }),
});

export type CreateShareParams = z.infer<typeof CreateShareParams>;
export type UpdateShareParams = z.infer<typeof UpdateShareParams>;
export type Share = z.infer<typeof nodeShareFieldSchema>;
export type AlbumShare = z.infer<typeof shareAlbumSchema>;

export type EditCreateShareModalProps =
  | {
      mode: 'create';
      nodeId: string;
      shareType: DAMNodeType | NodeType | string;
      name?: string;
      description?: string | null;
      shareItem?: Partial<TheNode> | null;
      firstShare?: boolean;
      asset?: Asset | null;
    }
  | {
      mode: 'edit';
      nodeId: string;
      shareId: string;
      shareType: DAMNodeType | NodeType | string;
      shareItem?: Partial<TheNode> | null;
      asset?: Asset | null;
    };

type ShareFormSchemaOptions = {
  minDate?: Date;
  maxDate?: Date;
};

const buildMinDateMessage = (minDate: Date) => {
  const resolvedToday = startOfDay(new Date());
  if (isSameDay(minDate, resolvedToday)) {
    return 'Expiration date must be today or later.';
  }
  return `Expiration date must be on or after ${format(
    minDate,
    getDateTimeInputConfig(DateTimeGranularity.Day).format,
  )}.`;
};

const buildMaxDateMessage = (maxDate: Date) =>
  `Expiration date must be on or before ${format(maxDate, getDateTimeInputConfig(DateTimeGranularity.Day).format)}.`;

export const createShareFormSchema = ({ minDate, maxDate }: ShareFormSchemaOptions = {}) => {
  const resolvedMinDate = minDate ? startOfDay(minDate) : startOfDay(new Date());
  const resolvedMaxDate = maxDate ? startOfDay(maxDate) : undefined;

  return z.object({
    sharedWith: z
      .array(
        z.object({
          label: z.string().email(),
          value: z.string().email(),
        }),
      )
      .nullable()
      .optional(),
    expiresAt: z
      .string()
      .superRefine((value, ctx) => {
        const validationResult = validateDateInputString(value, {
          minYear: resolvedMinDate.getFullYear(),
          ...(resolvedMaxDate ? { maxYear: resolvedMaxDate.getFullYear() } : {}),
        });
        if (!validationResult.valid) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: validationResult.message ?? 'Invalid date format. Expected format is YYYY-MM-DD.',
          });
          return;
        }

        const parsedDate = parseDateTimeInput(value, DateTimeGranularity.Day);
        if (!parsedDate) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Invalid date format. Expected format is YYYY-MM-DD.',
          });
          return;
        }

        if (resolvedMinDate && isBefore(parsedDate, resolvedMinDate)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: buildMinDateMessage(resolvedMinDate),
          });
          return;
        }

        if (resolvedMaxDate && isAfter(parsedDate, resolvedMaxDate)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: buildMaxDateMessage(resolvedMaxDate),
          });
        }
      })
      .nullable()
      .optional(),
    pendingEmails: z.array(z.string().email()).nullable().optional(),
    isPublic: z.boolean().nullable().optional(),
    collaborationEnabled: z.boolean().nullable().optional(),
    commentsVisibleFrom: z.boolean().nullable().optional(),
    includeOriginalFile: z.boolean().nullable().optional(),
    variants: z.object({
      preview: z.string().nullable().optional(),
      extra: z.array(z.string()).nullable().optional(),
    }),
  });
};

export const shareFormSchema = createShareFormSchema();
export type ShareFormValues = z.infer<ReturnType<typeof createShareFormSchema>>;

/** Aggregated common properties for a multi-selection inside a share; metadata is
 * an array of the shared public fields (same shape as the single-asset guest metadata),
 * optional since it's absent whenever none of the selected assets have public fields. */
export const shareMultipleAssetsSchema = z.object({
  assetsSelected: z.number(),
  name: z.string().nullable(),
  description: z.string().nullable(),
  tags: z.object({
    isFullIntersection: z.boolean(),
    tags: z.array(assetTagSchema),
  }),
  metadata: z.array(publicMetadataFieldSchema).optional(),
});

export type ShareMultipleAssets = z.infer<typeof shareMultipleAssetsSchema>;
